import {useMemo, useState, type ReactNode} from 'react'
import {ChevronGlyph} from '@/components/ui/icons'
import {Input} from '@/components/ui/input'
import {SearchComboboxShell} from '@/components/search-combobox-shell'
import {normalize} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

/** Combobox de selección única sobre una lista ya cargada; `clearLabel` ofrece elegir `null`. */
export function EntitySearchSelect<T>({
  items,
  value,
  getKey,
  filterText,
  renderOption,
  renderTriggerValue,
  placeholder,
  searchPlaceholder,
  noResultsLabel,
  clearLabel,
  freeTextLabel,
  onFreeText,
  testId,
  id,
  disabled,
  onQueryChange,
  onPick,
}: {
  items: T[]
  value: T | null
  getKey: (item: T) => string
  filterText: (item: T) => string
  renderOption: (item: T) => ReactNode
  renderTriggerValue: (item: T) => ReactNode
  placeholder: string
  searchPlaceholder: string
  noResultsLabel: string
  clearLabel?: string
  /** Solo donde un valor fuera de la lista es legítimo: ofrece adoptar lo tecleado tal cual. */
  freeTextLabel?: (texto: string) => string
  onFreeText?: (texto: string) => void
  testId: string
  /** Wired to the trigger button so an outside `<Label htmlFor>` still points somewhere focusable. */
  id?: string
  disabled?: boolean
  /** Para listas que no caben en memoria: el llamador manda lo tecleado como `search`; el filtro
   * local sigue corriendo encima. */
  onQueryChange?: (query: string) => void
  onPick: (item: T | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const normalizedQuery = normalize(query)
  const matches = useMemo(
    () => items.filter((item) => normalize(filterText(item)).includes(normalizedQuery)),
    [items, filterText, normalizedQuery]
  )

  function escribir(texto: string) {
    setQuery(texto)
    onQueryChange?.(texto)
  }

  function handleOpenChange(next: boolean) {
    if (disabled) return
    setOpen(next)
    if (!next) escribir('')
  }

  function pick(item: T | null) {
    onPick(item)
    setOpen(false)
    escribir('')
  }

  function adoptarTexto(texto: string) {
    onFreeText?.(texto)
    setOpen(false)
    escribir('')
  }

  return (
    <SearchComboboxShell
      title={placeholder}
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <button
          type="button"
          id={id}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          data-testid={`${testId}-trigger`}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-60 md:h-9">
          {value ? (
            <span className="min-w-0 truncate">{renderTriggerValue(value)}</span>
          ) : (
            <span className="min-w-0 truncate text-muted-foreground">{placeholder}</span>
          )}
          <ChevronGlyph size={14} className="opacity-60" />
        </button>
      }>
      <Input
        maxLength={tope('BUSCA')}
        placeholder={searchPlaceholder}
        value={query}
        onChange={(e) => escribir(e.target.value)}
        autoComplete="off"
        autoFocus
        data-testid={`${testId}-search`}
        className="h-12 shrink-0 rounded-none border-0 border-b border-border text-base focus-visible:ring-0 md:h-9 md:text-sm"
      />
      <ul className="flex-1 overflow-y-auto p-1 md:max-h-56 md:flex-none">
        {freeTextLabel && onFreeText && query.trim() !== '' && (
          <li>
            <button
              type="button"
              data-testid={`${testId}-libre`}
              className="w-full rounded-md px-2 py-3 text-left text-sm break-words hover:bg-secondary md:py-1.5"
              onClick={() => adoptarTexto(query.trim())}>
              {freeTextLabel(query.trim())}
            </button>
          </li>
        )}
        {clearLabel && value && (
          <li>
            <button
              type="button"
              data-testid={`${testId}-clear`}
              className="w-full rounded-md px-2 py-3 text-left text-sm text-muted-foreground hover:bg-secondary md:py-1.5"
              onClick={() => pick(null)}>
              {clearLabel}
            </button>
          </li>
        )}
        {matches.map((item) => (
          <li key={getKey(item)}>
            <button
              type="button"
              data-testid={`${testId}-option`}
              className="w-full break-words rounded-md px-2 py-3 text-left text-sm hover:bg-secondary md:py-1.5"
              onClick={() => pick(item)}>
              {renderOption(item)}
            </button>
          </li>
        ))}
        {matches.length === 0 && <li className="px-2 py-1.5 text-sm text-muted-foreground">{noResultsLabel}</li>}
      </ul>
    </SearchComboboxShell>
  )
}
